import { IkeUnitType } from '@ikegps/core-api';
import { makeObservable, observable } from 'mobx';
import { Geometry3D } from '../../../math';
import type { Size } from '../../../math/util';
import { Vector } from '../../../math/vector';
import {
	CollectionModelDrawable,
	CollectionModelDrawableLabel,
	CollectionModelDrawableLine,
	CollectionModelDrawableVerticalSeparation,
} from '../../../model';
import { IkeUnit } from '../../../unit/unit';
import { IkeDraw } from '../../draw';
import { IkeDrawLabel } from '../../drawable/util.draw.svg';
import { m } from '../../mithril';
import {
	CALIPER_INNERLINE_STROKE,
	CALIPER_INNERLINE_STROKE_WIDTH,
	CALIPER_OUTERLINE_STROKE,
	CALIPER_OUTERLINE_STROKE_WIDTH,
	CSS,
	INNERLINE_STROKE,
	INNERLINE_STROKE_WIDTH,
	OUTERLINE_STROKE,
	OUTERLINE_STROKE_WIDTH,
	Style,
} from './zoom.style';

export interface ZoomContext {
	zoomLevel: number;
	size: Size;
	useCrosshair: boolean;
	crosshairCenterGap: number;
}

export class ZoomRenderer {
	static CSS = CSS;
	static Style = Style;

	dc: IkeDraw;
	@observable zoomLevel = 1;

	@observable width = 0;
	@observable height = 0;
	@observable useCrosshair = true;
	@observable useWireCalipers = true;
	@observable crosshairCenterGap = 4;

	constructor(ctx: ZoomContext) {
		makeObservable(this);

		this.width = ctx.size.width || 200;
		this.height = ctx.size.height || 200;
		this.zoomLevel = ctx.zoomLevel || 1;
		this.useCrosshair = ctx.useCrosshair == null ? true : ctx.useCrosshair;
	}

	get isShown() {
		if (this.dc == null) {
			return false;
		}

		const current = this.dc.state.selectedId;
		if (current == null) {
			return false;
		}

		return true;
	}

	calculateCoordinate(offset: number, which: string) {
		const max: number = this.dc.capture.resolution[which];
		const min: number = 0;
		let calculatedOffset: number = Math.max(min, offset);
		calculatedOffset = Math.min(max, calculatedOffset);
		calculatedOffset = calculatedOffset * this.zoomLevel - this[which] / 2;
		return -calculatedOffset;
	}

	getZoomDrawable(dc: IkeDraw): CollectionModelDrawable | null {
		const selected = dc.selected;
		if (selected == null) {
			return null;
		}

		if (CollectionModelDrawableLine.isLine(selected)) {
			if (dc.state.selectedRegion === IkeDrawLabel.LABEL) {
				return selected.start;
			}
		}
		if (
			selected instanceof CollectionModelDrawableLabel &&
			selected.parent instanceof CollectionModelDrawableVerticalSeparation
		) {
			return null;
		}

		return selected;
	}

	getPixelOffset(wireSizeInches: number) {
		const wireSize = IkeUnit.convert(wireSizeInches, IkeUnit.METER, IkeUnit.INCH, IkeUnitType.DISTANCE); //inches to meters
		const moveUpXY = new Vector(0.0, -wireSize / 2.0); // in meters :zero in width, up 1/2 selected wire;
		const moveDownXY = new Vector(0.0, wireSize / 2.0); // in meters :zero in width, down 1/2 selected wire;

		return { moveUpXY, moveDownXY };
	}

	getWirePx(wireSizeInches: number, drawMarkerX: number, drawMarkerY: number, geoDraw: Geometry3D) {
		//the full size picture coordinates of the center of the zoom window
		const drawXY = new Vector(drawMarkerX, drawMarkerY);
		const halfHeight = this.height / 2.0;
		const halfWidth = this.width / 2.0;

		//gets the moved pixel value of the full picture
		const geoMoveUp = geoDraw.movePoint(drawXY, this.getPixelOffset(wireSizeInches).moveUpXY);
		const geoMoveDown = geoDraw.movePoint(drawXY, this.getPixelOffset(wireSizeInches).moveDownXY);

		//calculates the equivalency in the zoom window
		const MoveUp = new Vector(halfWidth, halfHeight - this.zoomLevel * Math.abs(drawMarkerY - geoMoveUp.y));
		const MoveDown = new Vector(halfWidth, halfHeight + this.zoomLevel * Math.abs(drawMarkerY - geoMoveDown.y));

		return { MoveUp, MoveDown };
	}

	view(dc: IkeDraw) {
		this.dc = dc;

		if (this.isShown === false) {
			return;
		}

		const drawable = this.getZoomDrawable(dc);
		if (drawable == null) {
			return;
		}

		this.useWireCalipers = this.dc.state.showCablemeasure;

		const offsetX = drawable.x;
		const offsetY = drawable.y;
		const capture = this.dc.capture;
		const src = capture.file.url;
		const resolution = capture.resolution;

		const theImage = m('img', {
			src,
			className: CSS.image,
			style:
				`transform: scale(${this.zoomLevel}); ` +
				`filter: url(#${dc.id}:brightness); ` +
				`left: ${this.calculateCoordinate(offsetX, 'width')}px; ` +
				`top: ${this.calculateCoordinate(offsetY, 'height')}px;`,
			width: resolution.width,
			height: resolution.height,
		});

		const containerChildren = [theImage];

		if (this.useCrosshair) {
			const halfHeight = this.height / 2;
			const halfWidth = this.width / 2;
			const crosshairElements = [];

			if (this.useWireCalipers) {
				// Left to center
				crosshairElements.push(
					m('line', {
						className: CSS.crosshairOuterline,
						stroke: OUTERLINE_STROKE,
						'stroke-width': OUTERLINE_STROKE_WIDTH,
						x1: 0,
						y1: halfHeight,
						x2: halfWidth / 16, //halfWidth - this.crosshairCenterGap,
						y2: halfHeight,
					})
				);

				crosshairElements.push(
					m('line', {
						className: CSS.crosshairInnerline,
						stroke: INNERLINE_STROKE,
						'stroke-width': INNERLINE_STROKE_WIDTH,
						x1: 0,
						y1: halfHeight,
						x2: halfWidth / 16, //halfWidth - this.crosshairCenterGap,
						y2: halfHeight,
					})
				);

				crosshairElements.push(
					m('line', {
						className: CSS.crosshairOuterline,
						stroke: OUTERLINE_STROKE,
						'stroke-width': OUTERLINE_STROKE_WIDTH,
						x1: halfWidth - halfWidth / 16,
						y1: halfHeight,
						x2: halfWidth - this.crosshairCenterGap,
						y2: halfHeight,
					})
				);

				crosshairElements.push(
					m('line', {
						className: CSS.crosshairInnerline,
						stroke: INNERLINE_STROKE,
						'stroke-width': INNERLINE_STROKE_WIDTH,
						x1: halfWidth - halfWidth / 16,
						y1: halfHeight,
						x2: halfWidth - this.crosshairCenterGap,
						y2: halfHeight,
					})
				);

				// Right to center
				crosshairElements.push(
					m('line', {
						className: CSS.crosshairOuterline,
						stroke: OUTERLINE_STROKE,
						'stroke-width': OUTERLINE_STROKE_WIDTH,
						x1: this.width - halfWidth / 16, //halfWidth + this.crosshairCenterGap,
						y1: halfHeight,
						x2: this.width,
						y2: halfHeight,
					})
				);

				crosshairElements.push(
					m('line', {
						className: CSS.crosshairInnerline,
						stroke: INNERLINE_STROKE,
						'stroke-width': INNERLINE_STROKE_WIDTH,
						x1: this.width - halfWidth / 16, //halfWidth + this.crosshairCenterGap,
						y1: halfHeight,
						x2: this.width,
						y2: halfHeight,
					})
				);

				crosshairElements.push(
					m('line', {
						className: CSS.crosshairOuterline,
						stroke: OUTERLINE_STROKE,
						'stroke-width': OUTERLINE_STROKE_WIDTH,
						x1: halfWidth + this.crosshairCenterGap,
						y1: halfHeight,
						x2: halfWidth + halfWidth / 16,
						y2: halfHeight,
					})
				);

				crosshairElements.push(
					m('line', {
						className: CSS.crosshairInnerline,
						stroke: INNERLINE_STROKE,
						'stroke-width': INNERLINE_STROKE_WIDTH,
						x1: halfWidth + this.crosshairCenterGap,
						y1: halfHeight,
						x2: halfWidth + halfWidth / 16,
						y2: halfHeight,
					})
				);
			} else {
				// Left to right
				crosshairElements.push(
					m('line', {
						className: CSS.crosshairOuterline,
						stroke: OUTERLINE_STROKE,
						'stroke-width': OUTERLINE_STROKE_WIDTH,
						x1: 0,
						y1: halfHeight,
						x2: halfWidth - this.crosshairCenterGap,
						y2: halfHeight,
					})
				);

				crosshairElements.push(
					m('line', {
						className: CSS.crosshairInnerline,
						stroke: INNERLINE_STROKE,
						'stroke-width': INNERLINE_STROKE_WIDTH,
						x1: 0,
						y1: halfHeight,
						x2: halfWidth - this.crosshairCenterGap,

						y2: halfHeight,
					})
				);

				// Right to left
				crosshairElements.push(
					m('line', {
						className: CSS.crosshairOuterline,
						stroke: OUTERLINE_STROKE,
						'stroke-width': OUTERLINE_STROKE_WIDTH,
						x1: halfWidth + this.crosshairCenterGap,
						y1: halfHeight,
						x2: this.width,
						y2: halfHeight,
					})
				);

				crosshairElements.push(
					m('line', {
						className: CSS.crosshairInnerline,
						stroke: INNERLINE_STROKE,
						'stroke-width': INNERLINE_STROKE_WIDTH,
						x1: halfWidth + this.crosshairCenterGap,
						y1: halfHeight,
						x2: this.width,
						y2: halfHeight,
					})
				);
			}
			// Top to bottom
			crosshairElements.push(
				m('line', {
					className: CSS.crosshairOuterline,
					stroke: OUTERLINE_STROKE,
					'stroke-width': OUTERLINE_STROKE_WIDTH,
					x1: halfWidth,
					y1: 0,
					x2: halfWidth,
					y2: halfHeight - this.crosshairCenterGap,
				})
			);

			crosshairElements.push(
				m('line', {
					className: CSS.crosshairInnerline,
					stroke: INNERLINE_STROKE,
					'stroke-width': INNERLINE_STROKE_WIDTH,
					x1: halfWidth,
					y1: 0,
					x2: halfWidth,
					y2: halfHeight - this.crosshairCenterGap,
				})
			);

			// Bottom to top
			crosshairElements.push(
				m('line', {
					className: CSS.crosshairOuterline,
					stroke: OUTERLINE_STROKE,
					'stroke-width': OUTERLINE_STROKE_WIDTH,
					x1: halfWidth,
					y1: halfHeight + this.crosshairCenterGap,
					x2: halfWidth,
					y2: this.height,
				})
			);

			crosshairElements.push(
				m('line', {
					className: CSS.crosshairInnerline,
					stroke: INNERLINE_STROKE,
					'stroke-width': INNERLINE_STROKE_WIDTH,
					x1: halfWidth,
					y1: halfHeight + this.crosshairCenterGap,
					x2: halfWidth,
					y2: this.height,
				})
			);

			const crosshairSVG = m(
				'svg',
				{
					className: CSS.crosshair,
					width: '100%',
					height: '100%',
					viewBox: '0 0 ' + this.width + ' ' + this.height,
				},
				crosshairElements
			);

			containerChildren.push(crosshairSVG);
		}

		//VERSION 3
		if (this.useWireCalipers) {
			const center_x = this.width / 2;
			const caliperElements = [];
			const caliperLoc = this.getWirePx(
				dc.state.cablemeasure,
				drawable.x,
				drawable.y,
				dc.drawState.collection.state.getGeometry(drawable.captureId)
			);
			const moveUp = caliperLoc.MoveUp;
			const moveDown = caliperLoc.MoveDown;

			//upper caliper
			caliperElements.push(
				m('line', {
					className: CSS.crosshairOuterline,
					stroke: CALIPER_OUTERLINE_STROKE,
					'stroke-width': CALIPER_OUTERLINE_STROKE_WIDTH,
					x1: center_x / 16,
					y1: moveUp.y,
					x2: center_x - center_x / 16,
					y2: moveUp.y,
				})
			);
			caliperElements.push(
				m('line', {
					className: CSS.crosshairInnerline,
					stroke: CALIPER_INNERLINE_STROKE,
					'stroke-width': CALIPER_INNERLINE_STROKE_WIDTH,
					x1: center_x / 16,
					y1: moveUp.y,
					x2: center_x - center_x / 16,
					y2: moveUp.y,
				})
			);

			caliperElements.push(
				m('line', {
					className: CSS.crosshairOuterline,
					stroke: CALIPER_OUTERLINE_STROKE,
					'stroke-width': CALIPER_OUTERLINE_STROKE_WIDTH,
					x1: center_x + center_x / 16,
					y1: moveUp.y,
					x2: this.width - center_x / 16,
					y2: moveUp.y,
				})
			);
			caliperElements.push(
				m('line', {
					className: CSS.crosshairInnerline,
					stroke: CALIPER_INNERLINE_STROKE,
					'stroke-width': CALIPER_INNERLINE_STROKE_WIDTH,
					x1: center_x + center_x / 16,
					y1: moveUp.y,
					x2: this.width - center_x / 16,
					y2: moveUp.y,
				})
			);

			//lower caliper
			caliperElements.push(
				m('line', {
					className: CSS.crosshairOuterline,
					stroke: CALIPER_OUTERLINE_STROKE,
					'stroke-width': CALIPER_OUTERLINE_STROKE_WIDTH,
					x1: center_x / 16,
					y1: moveDown.y,
					x2: center_x - center_x / 16,
					y2: moveDown.y,
				})
			);
			caliperElements.push(
				m('line', {
					className: CSS.crosshairInnerline,
					stroke: CALIPER_INNERLINE_STROKE,
					'stroke-width': CALIPER_INNERLINE_STROKE_WIDTH,
					x1: center_x / 16,
					y1: moveDown.y,
					x2: center_x - center_x / 16,
					y2: moveDown.y,
				})
			);

			caliperElements.push(
				m('line', {
					className: CSS.crosshairOuterline,
					stroke: CALIPER_OUTERLINE_STROKE,
					'stroke-width': CALIPER_OUTERLINE_STROKE_WIDTH,
					x1: center_x + center_x / 16,
					y1: moveDown.y,
					x2: this.width - center_x / 16,
					y2: moveDown.y,
				})
			);
			caliperElements.push(
				m('line', {
					className: CSS.crosshairInnerline,
					stroke: CALIPER_INNERLINE_STROKE,
					'stroke-width': CALIPER_INNERLINE_STROKE_WIDTH,
					x1: center_x + center_x / 16,
					y1: moveDown.y,
					x2: this.width - center_x / 16,
					y2: moveDown.y,
				})
			);

			caliperElements.push(
				m(
					'text',
					{
						className: CSS.crosshairCaliper,
						fill: 'white',
						stroke: 'black',
						x: center_x / 2,
						y: moveUp.y - 15,
						'text-anchor': 'middle',
						'font-size': 20,
						'alignment-baseline': 'middle',
						style: {
							'user-select': 'none',
							'pointer-events': 'none',
							'font-weight': 'bold',
						},
					},
					this.dc.state.cablemeasure + " '' "
				)
			);

			const crosshairSVG = m(
				'svg',
				{
					className: CSS.crosshair,
					width: '100%',
					height: '100%',
					viewBox: '0 0 ' + this.width + ' ' + this.height,
				},
				caliperElements
			);

			containerChildren.push(crosshairSVG);
		}

		const theContainer = m(
			'div',
			{
				className: CSS.container,
				style: 'width: ' + this.width + 'px; height: ' + this.height + 'px; overflow: hidden;',
			},
			containerChildren
		);

		return theContainer;
	}
}
